import React, { useState, useEffect, useCallback } from "react";
import Logo from "../components/Logo";
import PopUp, { PopupSize } from "../components/Popup";
import RightSectionLogin from "../components/RightSectionLogin";
import { NavLink, useNavigate } from "react-router-dom";
import SignupSectionButtons from "../components/Auth/SignupSectionButtons";
import SideMenu, { ModeType } from "../components/SideMenu";
import {
  useWallet,
  WalletStatus,
  SignBytesFailed,
  SignBytesResult,
  Timeout,
  useConnectedWallet,
  UserDenied,
} from "@terra-money/wallet-provider";
import ModalAuth from "../components/Auth/ModalAuth";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import Button, { ButtonSize, ButtonType } from "components/Button";
import {
  AuthFlow,
  ConnectWallet,
  WalletTypes,
} from "../components/Auth/ConnectWallet";
import { TermsValidation } from "../components/Auth/TermsValidation";
import { useContextUser } from "contexts/user";
import Loader, { LoaderType } from "components/Loader";
import { MsgExecuteContract, MsgSend } from "@terra-money/terra.js";
import { instanceOfUserData } from "utils/instanceOf";
import jscrypto from "jscrypto";
import { useContextBlockchainData } from "contexts/blockchain-data";
import { SignResult } from "@terra-dev/wallet-types";
import { terra } from "shared/Terra";
import { Warning } from "components/Modals";

export enum SignUpStep {
  connectWallet = "connectWallet",
  verifyWallet = "verifyWallet",
  signUpCompleted = "signUpCompleted",
}

export enum ModalState {
  verify = "verify",
  success = "success",
  failure = "failure",
  hasUser = "hasUser",
  noUser = "noUser",
  noSocial = "noSocial",
}

export enum Network {
  columbus5 = "columbus-5",
  phoenix1 = "phoenix-1",
  pisco1 = "pisco-1",
}

/* Sign in component receives as props from the App the global variable that holds user data, updates it and sends back */
export const SignInPage: React.FC = () => {
  const { t } = useTranslation(["common", "enumerations"]);
  const { status, disconnect, wallets } = useWallet();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isTermsChecked, setIsTermsChecked] = useState(
    status === WalletStatus.WALLET_CONNECTED
  );
  const [width, setWidth] = useState(window.innerWidth);
  const [currentStep, setCurrentStep] = useState(
    status === WalletStatus.WALLET_CONNECTED
      ? SignUpStep.verifyWallet
      : SignUpStep.connectWallet
  );
  const [modalContent, setModalContent] = useState<null | ModalState>(null);
  const [randomBytes, setRandomBytes] = useState("");
  const [txError, setTxError] = useState<string | null>(null);
  const [tempUserData, setTempUserData] = useState(null);

  const connectedWallet = useConnectedWallet();
  const { blockchains } = useContextBlockchainData();
  const { setUser } = useContextUser();

  const updateWidth = () => {
    setWidth(window.innerWidth);
  };

  const handleModal = (content: any) => {
    setIsOpenModal && setIsOpenModal(true);
    setModalContent(content);
  };

  const handleStep = (step: any) => {
    setCurrentStep && setCurrentStep(step);
  };

  const handleSetUser = () => {
    setTimeout(() => {
      setIsOpenModal(false);
      setModalContent(null);
      setUser(tempUserData);
    }, 3000);
  };

  const handleConnectWallet = async () => {
    setCurrentStep(SignUpStep.connectWallet);
    setIsOpenModal(true);
  };

  const handleVerifyWallet = async () => {
    try {
      const connection = await fetch(
        `${process.env.REACT_APP_MIDDLEWARE_NEAR_URL}/fauna/auth/bytes`,
        {
          method: "POST",
          body: JSON.stringify({
            walletAddress: connectedWallet?.terraAddress,
          }),
          credentials: "include",
        }
      );
      const dataFromApi = await connection.json();
      setRandomBytes(dataFromApi.data.bytes);
      if (connectedWallet?.connection.identifier === WalletTypes.station) {
        await signBytes(dataFromApi.data.bytes);
      } else if (
        connectedWallet?.connection.identifier !== WalletTypes.station
      ) {
        await executeContract();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleLoggedInUser = async (
    walletAddress: string,
    signBytesResult: any,
    originalBytes: any
  ) => {
    setIsLoading(true);
    try {
      const connection = await fetch(
        `${process.env.REACT_APP_MIDDLEWARE_NEAR_URL}/fauna/auth/signup`,
        {
          method: "POST",
          body: JSON.stringify({
            walletAddress,
            chainId:
              blockchains?.filter(
                (b) => b.chain_tecnical_id === connectedWallet?.network.chainID
              )[0].chain_id || "zb1_002",
            signBytesResult,
            originalBytes,
          }),
          credentials: "include",
        }
      );
      const dataFromApi = await connection.json();
      if (
        typeof dataFromApi.data === "object" &&
        instanceOfUserData(dataFromApi.data)
      ) {
        setTempUserData(dataFromApi.data);
        handleModal(ModalState.success);
        handleStep(SignUpStep.signUpCompleted);
      } else if (typeof dataFromApi.data === "string") {
        handleModal(ModalState.hasUser);
      } else {
        handleModal(ModalState.failure);
      }
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  };

  const getContract = (contract: string) => {
    switch (contract) {
      case Network.columbus5:
        return process.env.REACT_APP_CONTRACT_TERRA_CLASSIC || "";
      case Network.phoenix1:
        return process.env.REACT_APP_CONTRACT_TERRA || "";
      default:
        return process.env.REACT_APP_CONTRACT_TERRA || "";
    }
  };

  const signBytes = useCallback(
    async (originalBytes) => {
      if (!connectedWallet) {
        return;
      }
      try {
        const bufferedBytes = Buffer.from(randomBytes);
        /* Sign bytes with bytes fetched from API to this specific wallet */
        const signedBytes: SignBytesResult = await connectedWallet.signBytes(
          bufferedBytes
        );
        /* Send information from signing to API to verify the transaction */
        handleLoggedInUser(
          connectedWallet.terraAddress,
          signedBytes.result,
          originalBytes
        );
      } catch (error) {
        if (error instanceof UserDenied) {
          setTxError("User Denied");
        } else if (error instanceof Timeout) {
          setTxError("Timeout");
        } else if (error instanceof SignBytesFailed) {
          setTxError("Sign Bytes Failed");
        } else {
          setTxError(
            "Unknown Error: " +
              (error instanceof Error ? error.message : String(error))
          );
        }
        handleModal(ModalState.failure);
      }
    },
    [connectedWallet]
  );

  const executeContract = async () => {
    if (connectedWallet) {
      let executeTx: SignResult;
      try {
        const execute = new MsgExecuteContract(
          connectedWallet.walletAddress,
          getContract(connectedWallet?.network.chainID),
          { login: {} }
        );

        executeTx = await connectedWallet.sign({
          msgs: [execute],
        });

        const executeTxResult = await terra.tx.broadcast(executeTx.result);
        // if (executeTx) {
        //   /* If wallet is connected and verified, fetch wallet address and send to setUser function */
        //   setLoggedInUser(wallets[0].terraAddress);
        // }

        // if (!executeTx) {
        //   /* alert("Could not sign transaction properly"); */
        //   /* If wallet is connected but not correctly verified, send to setUser function information to not allow auths */
        //   setModalContent(ModalState.failure);
        //   setIsOpenModal(true);
        //   return;
        // }
      } catch (error: unknown) {
        handleModal(ModalState.failure);
      }
    }
  };

  const handleDisconnect = () => {
    disconnect();
    setIsOpenModal(false);
    setModalContent(null);
  };

  const handleLogin = () => {
    setIsOpenModal(false);
    setModalContent(null);
    navigate("/");
  };

  const handleResetModal = () => {
    setTimeout(() => {
      setIsOpenModal(false);
      setModalContent(null);
    }, 2000);
  };

  const handleWarningText = () => {
    if (txError) {
      return txError;
    } else {
      return t("modal_content.failure.modal_title", {
        ns: "enumerations",
      });
    }
  };

  const handleTryAgain = () => {
    setTimeout(() => {
      disconnect();
      setCurrentStep(SignUpStep.connectWallet);
      setIsOpenModal(false);
      setModalContent(null);
    }, 3000);
  };

  useEffect(() => {
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const existUrlParams = window.location.search.includes("?");

    if (existUrlParams) {
      const accountId = urlParams.get("account_id");
      const loginUser = async () => {
        setIsLoading(true);
        try {
          const connection = await fetch(
            `${process.env.REACT_APP_MIDDLEWARE_NEAR_URL}/fauna/auth/signup-near`,
            {
              method: "POST",
              body: JSON.stringify({
                walletAddress: accountId,
                chainId: "zb1_003",
              }),
              credentials: "include",
            }
          );
          const dataFromApi = await connection.json();
          if (
            typeof dataFromApi.data === "object" &&
            instanceOfUserData(dataFromApi.data)
          ) {
            setTempUserData(dataFromApi.data);
            handleModal(ModalState.success);
            handleStep(SignUpStep.signUpCompleted);
          } else if (typeof dataFromApi.data === "string") {
            handleModal(ModalState.hasUser);
          } else {
            handleModal(ModalState.failure);
          }
        } catch (error) {
          console.error(error);
        }
        setIsLoading(false);
      };
      loginUser();
    }
  }, []);

  return (
    <>
      {isLoading && <Loader type={LoaderType.fullScreen} />}
      <div className="flex flex-grow">
        <SideMenu authPage={true} mode={ModeType.wide}>
          <Logo />
          <h2 className="self-center text-lg md:text-xl">Sign Up</h2>
          <SignupSectionButtons
            walletStatus={status}
            onConnectWalletClick={handleConnectWallet}
            onVerifyWalletClick={handleVerifyWallet}
            currentStep={currentStep}
          />
          <p className="self-center static mt-10 text-sm md:text-base">
            {t("modal_content.has_account", { ns: "enumerations" })}
            <NavLink to="/" className={"text-muddywaters-500"}>
              Log in
            </NavLink>
          </p>
        </SideMenu>

        <RightSectionLogin />

        {isOpenModal && (
          <>
            {currentStep === SignUpStep.connectWallet && (
              <PopUp
                size={PopupSize.large}
                closeModal={() => setIsOpenModal(false)}
              >
                {width < 1024 && (
                  <>
                    {!isTermsChecked && (
                      <ModalAuth
                        modalTitle={t(
                          "modal_content.connect_hermes.modal_title",
                          { ns: "enumerations" }
                        )}
                        additionalTitle={t(
                          "modal_content.connect_hermes.additional_title",
                          { ns: "enumerations" }
                        )}
                        logo={true}
                      >
                        <TermsValidation
                          setIsTermsChecked={setIsTermsChecked}
                          isTermsChecked={isTermsChecked}
                        />
                      </ModalAuth>
                    )}
                    {isTermsChecked && (
                      <div className="flex flex-col">
                        <ConnectWallet
                          setCurrentStep={setCurrentStep}
                          setIsOpenModal={setIsOpenModal}
                          setModalContent={setModalContent}
                          modalContent={modalContent}
                          authFlow={AuthFlow.signup}
                        />
                        <div id="back-button">
                          <Button
                            type={ButtonType.cancel}
                            size={ButtonSize.small}
                            onClick={() => setIsTermsChecked(false)}
                          >
                            <div>
                              <FontAwesomeIcon icon={faChevronLeft} />
                            </div>
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {width >= 1024 && (
                  <ModalAuth
                    modalTitle={t("modal_content.connect_hermes.modal_title", {
                      ns: "enumerations",
                    })}
                    additionalTitle={t(
                      "modal_content.connect_hermes.additional_title",
                      { ns: "enumerations" }
                    )}
                    logo={true}
                  >
                    <TermsValidation
                      setIsTermsChecked={setIsTermsChecked}
                      isTermsChecked={isTermsChecked}
                    />
                    {isTermsChecked && (
                      <ConnectWallet
                        setCurrentStep={setCurrentStep}
                        setIsOpenModal={setIsOpenModal}
                        setModalContent={setModalContent}
                        modalContent={modalContent}
                        authFlow={AuthFlow.signup}
                      />
                    )}
                  </ModalAuth>
                )}
              </PopUp>
            )}
            {modalContent === ModalState.verify && (
              <PopUp size={PopupSize.large}>
                <ModalAuth
                  modalTitle={t("modal_content.verify_wallet.modal_title", {
                    ns: "enumerations",
                  })}
                  additionalTitle={t(
                    "modal_content.verify_wallet.additional_title",
                    {
                      ns: "enumerations",
                    }
                  )}
                  logo={true}
                >
                  {handleResetModal()}
                </ModalAuth>
              </PopUp>
            )}
            {modalContent === ModalState.success && (
              <PopUp size={PopupSize.large}>
                <ModalAuth
                  modalTitle={t("modal_content.verified_wallet", {
                    ns: "enumerations",
                  })}
                  logo={true}
                >
                  {handleSetUser()}
                </ModalAuth>
              </PopUp>
            )}
            {modalContent === ModalState.failure && (
              <PopUp size={PopupSize.large}>
                <Warning
                  title={"Warning"}
                  text={handleWarningText()}
                  /* additionalText={t("modal_content.failure.additional_title", {
                    ns: "enumerations",
                  })} */
                  iconWarning={true}
                />
                {handleTryAgain()}
              </PopUp>
            )}
            {modalContent === ModalState.hasUser && (
              <PopUp size={PopupSize.large}>
                <ModalAuth
                  modalTitle={t(
                    "modal_content.user_already_exists.modal_title",
                    {
                      ns: "enumerations",
                    }
                  )}
                  additionalTitle={t(
                    "modal_content.user_already_exists.additional_title",
                    { ns: "enumerations" }
                  )}
                  logo={true}
                  primaryButton={t(
                    "modal_content.user_already_exists.primary_button",
                    { ns: "enumerations" }
                  )}
                  secondaryButton={t(
                    "modal_content.user_already_exists.secondary_button",
                    { ns: "enumerations" }
                  )}
                  primaryButtonOnClick={handleDisconnect}
                  secondaryButtonOnClick={handleLogin}
                ></ModalAuth>
              </PopUp>
            )}
          </>
        )}
      </div>
    </>
  );
};
